import { useCallback, useEffect, useMemo, useState } from 'react'

import { api } from '../../lib/api'
import { DatePicker, DOW_TH_MON, MONTH_TH, monthGrid } from '../../lib/DatePicker'
import { employeesApi, type EmployeeRow } from '../../lib/employees'
import { useAuthedSession } from '../../lib/SessionContext'

/* ============================================================================
   สร้างตารางกะให้พนักงาน · backend: src/routes/adminShiftPlan.js

   ⚠️ เขียนลง HR_EMP_WORK ของ production — ตารางที่ระบบคิดเวลาทำงานอ่านทุกวัน

   หน้าเดียวจบ 2 คอลัมน์: ซ้ายเลือกคน ขวาเลือกกะ แล้วปฏิทินขึ้นเองด้านล่าง
   (เคยทำเป็น wizard 3 ขั้น แล้วพบว่าต้องเดินหน้าถอยหลังเพื่อแก้กะทีละครั้ง
    ทั้งที่ของที่ต้องเห็นพร้อมกันคือ "เลือกใคร" กับ "ได้ตารางแบบไหน")

   ⚠️ พรีวิวกับของจริงใช้ตัวคิดแผนตัวเดียวกันที่ backend (buildPlan) —
      ห้ามคิดแผนซ้ำที่ฝั่งนี้ ไม่งั้นพรีวิวจะโกหกได้เมื่อสองฝั่งเพี้ยนกัน
   ============================================================================ */

const BASE = '/api/admin/shift-plan'
const nf = new Intl.NumberFormat('th-TH')

type ShiftOption = {
  Shift_ID: number
  Shift_Desc: string
  Shift_Start: string | null
  Shift_End: string | null
  offDays: number
}

type CalendarOption = {
  CalendarID: number
  CalendarYear: number
  CalendarRemark: string
  /** backend ส่งมาด้วยเสมอ — ใช้กรองตามบริษัทที่เลือก */
  CompanyCode: string
  dayCount: number
}

type Skipped = { empCode: string; empName: string; date: string; reason: string }

type Preview = {
  from: string
  to: string
  summary: {
    employees: number
    days: number
    total: number
    workDays: number
    holidays: number
    skipped: number
    overwritePast: number
  }
  skipped: Skipped[]
  skippedMore: number
  sample: PlanDay[]
}

type PlanDay = {
  date: string
  slot: number
  isHoliday: boolean
  shiftName: string | null
  start: string | null
  end: string | null
  breakStart: string | null
  breakEnd: string | null
  needsRecalc: boolean
  skipped: boolean
  reason?: string
}

const p2 = (n: number) => String(n).padStart(2, '0')
const firstOfMonth = (d: Date) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-01`
const lastOfMonth = (d: Date) =>
  `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate())}`

export default function ShiftPlan() {
  const session = useAuthedSession()
  const mayWrite =
    session.kind === 'admin' && (session.role === 'superadmin' || session.role === 'admin')

  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [saving, setSaving] = useState(false)

  const [shifts, setShifts] = useState<ShiftOption[]>([])
  const [calendars, setCalendars] = useState<CalendarOption[]>([])
  const [minDate, setMinDate] = useState('')
  const [companies, setCompanies] = useState<{ code: string; name: string | null }[]>([])

  /* บริษัทที่กำลังทำ — กรองทั้งรายชื่อพนักงานและปฏิทินวันหยุด
     (พนักงานคนละบริษัทใช้ปฏิทินคนละใบ เลือกผสมกันแล้ววันหยุดจะผิดของใครคนหนึ่ง) */
  const [companyCode, setCompanyCode] = useState('')

  const today = new Date()
  const [from, setFrom] = useState(firstOfMonth(today))
  const [to, setTo] = useState(lastOfMonth(today))

  const [picked, setPicked] = useState<EmployeeRow[]>([])
  const [calendarId, setCalendarId] = useState('')
  /* แพทเทิร์นรายสัปดาห์ — ช่องที่ i คือสัปดาห์ที่ i+1 · null = หยุดทั้งสัปดาห์
     เป็นแหล่งความจริงเดียว ไม่มี mode/rotateWeeks แยกอีก (หมุนทุกสัปดาห์เสมอ
     ยาวเท่าไรก็วนเท่านั้น — 1 ช่อง = กะเดียวตลอด, 2 ช่อง = สลับสัปดาห์เว้นสัปดาห์) */
  const [shiftIds, setShiftIds] = useState<(number | null)[]>([])

  const [preview, setPreview] = useState<Preview | null>(null)
  const [done, setDone] = useState<{ written: number; recalculated: number; skipped: number } | null>(
    null,
  )

  useEffect(() => {
    api<{ shifts: ShiftOption[]; calendars: CalendarOption[]; minDate: string }>(`${BASE}/options`)
      .then((d) => {
        setShifts(d.shifts)
        setCalendars(d.calendars)
        setMinDate(d.minDate)
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))

    api<{ options: { code: string; name: string | null }[] }>('/api/admin/companies/options')
      .then((d) => {
        setCompanies(d.options)
        /* มีบริษัทเดียวก็เลือกให้เลย — ไม่ต้องให้กดสิ่งที่ไม่มีทางเลือกอื่น */
        if (d.options.length === 1) setCompanyCode(d.options[0].code)
      })
      .catch(() => {})
  }, [])

  /* เปลี่ยนบริษัท = ของที่เลือกไว้ใช้ไม่ได้แล้ว ต้องล้าง
     ⚠️ ถ้าไม่ล้าง จะส่งพนักงานบริษัท ก. ไปพร้อมปฏิทินบริษัท ข. แล้ว backend
        รับไว้ด้วย (สองอย่างนี้ไม่ได้ตรวจคู่กัน) — วันหยุดจะผิดทั้งชุดแบบเงียบ ๆ */
  useEffect(() => {
    setPicked([])
    setCalendarId('')
  }, [companyCode])

  /* ปฏิทินของบริษัทที่เลือกเท่านั้น — ยังไม่เลือกบริษัทก็โชว์ทั้งหมด
     (กรณีมีบริษัทเดียว companyCode ถูกตั้งให้แล้วตั้งแต่ตอนโหลด) */
  const shownCalendars = useMemo(
    () => (companyCode ? calendars.filter((c) => c.CompanyCode === companyCode) : calendars),
    [calendars, companyCode],
  )

  /* เหลือใบเดียวก็เลือกให้ — ของจริงบริษัทหนึ่งมักมีปฏิทินปีละใบ
     แต่ "ปีหนึ่งมีได้หลายใบ" (แยกตามกลุ่มพนักงาน) จึงไม่เลือกให้เมื่อมีหลายใบ */
  useEffect(() => {
    if (shownCalendars.length === 1) setCalendarId(String(shownCalendars[0].CalendarID))
  }, [shownCalendars])

  const ready =
    picked.length > 0 &&
    from !== '' &&
    to !== '' &&
    shiftIds.length >= 1 &&
    shiftIds.some((v) => v !== null)

  const body = useMemo(
    () => ({
      empCodes: picked.map((p) => p.empCode),
      from,
      to,
      calendarId: calendarId ? Number(calendarId) : null,
      shiftIds,
      /* หมุนทุกสัปดาห์เสมอ — ความยาวของ pattern เป็นตัวกำหนดรอบเอง */
      rotateWeeks: 1,
    }),
    [picked, from, to, calendarId, shiftIds],
  )

  /* พรีวิวคิดเองทุกครั้งที่เปลี่ยนอะไร — หน่วงไว้ไม่ให้ยิงทุกการกด
     ใช้ endpoint เดิม ไม่ได้คิดแผนซ้ำฝั่งนี้ (ภาพจะได้ไม่โกหก) */
  useEffect(() => {
    if (!ready) {
      setPreview(null)
      return
    }
    let alive = true
    const t = setTimeout(() => {
      setBusy(true)
      api<Preview>(`${BASE}/preview`, { method: 'POST', body: JSON.stringify(body) })
        .then((d) => {
          if (!alive) return
          setPreview(d)
          setError(null)
        })
        .catch((e: unknown) => {
          if (!alive) return
          setPreview(null)
          setError(e instanceof Error ? e.message : String(e))
        })
        .finally(() => alive && setBusy(false))
    }, 400)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [ready, body])

  async function apply() {
    if (!preview) return
    const msg =
      `สร้างตารางกะ ${preview.from} ถึง ${preview.to}\n` +
      `พนักงาน ${nf.format(preview.summary.employees)} คน = ${nf.format(preview.summary.total)} แถว\n` +
      (preview.summary.overwritePast > 0
        ? `ทับวันที่มีเวลาตอกบัตรแล้ว ${nf.format(preview.summary.overwritePast)} วัน (จะคำนวณใหม่ให้)\n`
        : '') +
      (preview.summary.skipped > 0
        ? `ข้าม ${nf.format(preview.summary.skipped)} วันที่มีใบลาอนุมัติแล้ว\n`
        : '') +
      '\nดำเนินการต่อ?'
    if (!window.confirm(msg)) return

    setSaving(true)
    try {
      const d = await api<{ written: number; recalculated: number; skipped: number }>(
        `${BASE}/apply`,
        { method: 'POST', body: JSON.stringify(body) },
      )
      setDone(d)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  /** ตั้งช่วงเป็นทั้งปี — ปุ่มลัดที่ใช้บ่อยสุดตอนวางแผนต้นปี */
  function wholeYear(year: number) {
    setFrom(`${year}-01-01`)
    setTo(`${year}-12-31`)
  }
  const thisYear = today.getFullYear()

  return (
    <>
      <h1>สร้างตารางกะ</h1>

      {error ? <p className="error">{error}</p> : null}
      {done ? (
        <p className="ok">
          เขียนแล้ว {nf.format(done.written)} แถว
          {done.recalculated > 0 ? ` · คำนวณเวลาทำงานใหม่ ${nf.format(done.recalculated)} วัน` : ''}
          {done.skipped > 0 ? ` · ข้าม ${nf.format(done.skipped)} วัน` : ''}
        </p>
      ) : null}

      {companies.length > 1 ? (
        <div className="emp-toolbar">
          <label className="plan-company">
            บริษัท
            <select value={companyCode} onChange={(e) => setCompanyCode(e.target.value)}>
              <option value="">— เลือกบริษัท —</option>
              {companies.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name ? `${c.code} — ${c.name}` : c.code}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {companies.length > 1 && !companyCode ? (
        <p className="meta">เลือกบริษัทก่อน แล้วรายชื่อพนักงานกับปฏิทินวันหยุดจะกรองตามให้</p>
      ) : null}

      <div className="plan-page">
        <section>
          <EmployeePicker picked={picked} onChange={setPicked} companyCode={companyCode} />
        </section>

        <section>
          <p className="fieldset-title">ช่วงวันที่</p>
          <div className="emp-form">
            <div className="field-grid" style={{ gridTemplateColumns: '10rem 10rem minmax(0, 1fr)' }}>
              <label>
                ตั้งแต่
                <DatePicker value={from} onChange={setFrom} min={minDate} allowEmpty />
              </label>
              <label>
                ถึง
                <DatePicker value={to} onChange={setTo} min={from || minDate} allowEmpty />
              </label>
              <label>
                ปฏิทินวันหยุด
                <select value={calendarId} onChange={(e) => setCalendarId(e.target.value)}>
                  <option value="">ไม่ใช้ปฏิทิน (หยุดตามกะเท่านั้น)</option>
                  {shownCalendars.map((c) => (
                    <option key={c.CalendarID} value={c.CalendarID}>
                      {c.CalendarYear} — {c.CalendarRemark} ({c.dayCount} วัน)
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* ปุ่มลัด — ของจริงวางแผนเป็นปีหรือเป็นเดือน ไม่ใช่ช่วงพิสดาร */}
            <p className="plan-quick">
              <button type="button" className="ghost tiny" onClick={() => wholeYear(thisYear)}>
                ทั้งปี {thisYear}
              </button>{' '}
              <button type="button" className="ghost tiny" onClick={() => wholeYear(thisYear + 1)}>
                ทั้งปี {thisYear + 1}
              </button>{' '}
              <button
                type="button"
                className="ghost tiny"
                onClick={() => {
                  setFrom(firstOfMonth(today))
                  setTo(lastOfMonth(today))
                }}
              >
                เดือนนี้
              </button>
            </p>

            {/* เพดานย้อนหลังเป็นกฎความปลอดภัย ไม่ใช่ความสะดวก — backend กันซ้ำอีกชั้น */}
            <p className="field-note">
              ย้อนหลังได้ถึง {minDate} (วันที่ 1 ของเดือนที่แล้ว) · วันก่อนหน้านั้นมีเวลาตอกบัตรจริงอยู่
            </p>
          </div>

          <PatternEditor shifts={shifts} pattern={shiftIds} onChange={setShiftIds} />
        </section>
      </div>

      {ready ? (
        <>
          <p className="fieldset-title">
            ตั้งใหม่ — {picked[0]?.fullName ?? picked[0]?.empCode}
            {picked.length > 1 ? ` (จาก ${nf.format(picked.length)} คน)` : ''}
          </p>
          <PlanCalendar
            days={preview?.sample ?? null}
            loading={busy}
            shifts={shifts}
            shiftIds={shiftIds}
            multiple={shiftIds.length > 1}
            from={from}
            to={to}
            summary={preview?.summary}
          />

          {preview ? (
            <>
              <div className="plan-summary">
                <Stat label="พนักงาน" value={preview.summary.employees} unit="คน" />
                <Stat label="ช่วง" value={preview.summary.days} unit="วัน" />
                <Stat label="จะเขียน" value={preview.summary.total} unit="แถว" />
                <Stat label="วันทำงาน" value={preview.summary.workDays} unit="วัน/คน" />
                <Stat label="วันหยุด" value={preview.summary.holidays} unit="วัน/คน" />
              </div>

              {preview.summary.overwritePast > 0 ? (
                <p className="warn">
                  ทับวันที่มีเวลาตอกบัตรแล้ว {nf.format(preview.summary.overwritePast)} วัน —
                  ระบบจะคำนวณเวลาทำงานใหม่ให้อัตโนมัติหลังบันทึก
                </p>
              ) : null}

              {preview.skipped.length ? <SkippedList preview={preview} /> : null}

              {mayWrite ? (
                <p>
                  <button
                    type="button"
                    disabled={saving || busy || preview.summary.total === 0}
                    onClick={() => void apply()}
                  >
                    {saving ? 'กำลังบันทึก…' : `บันทึก ${nf.format(preview.summary.total)} แถว`}
                  </button>
                </p>
              ) : null}
            </>
          ) : null}
        </>
      ) : (
        <p className="meta">
          เลือกพนักงานและกะให้ครบเพื่อดูปฏิทิน

        </p>
      )}
    </>
  )
}


/* ถังขยะ 16px — ตัวเดียวกับหน้าวันหยุด (ที่นั่นก็วาดเอง ไม่ได้ลง icon lib) */
function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 7h16M10 11v6M14 11v6M5 7l1 13h12l1-13M9 7V4h6v3" strokeLinecap="round" />
    </svg>
  )
}

function PlanCalendar({
  days: dayList,
  loading,
  shifts,
  shiftIds,
  multiple,
  from,
  to,
  summary,
}: {
  /** null = ยังโหลดไม่เสร็จ · [] = ไม่มีข้อมูลในช่วงนี้ */
  days: PlanDay[] | null
  loading: boolean
  shifts: ShiftOption[]
  shiftIds: (number | null)[]
  multiple: boolean
  from: string
  to: string
  summary?: Preview['summary']
}) {
  const byDate = useMemo(() => new Map((dayList ?? []).map((d) => [d.date, d])), [dayList])

  /* กางทุกเดือนในช่วงเรียงลงมา ไม่ใช่ทีละเดือนแล้วให้กดเลื่อน
     เลือกทั้งปีแล้วต้องกด 12 ครั้งเพื่อดูให้ครบคือการทำงานที่ไม่ได้อะไรกลับมา
     ⚠️ guard 24 เดือน — ช่วงถูกจำกัดที่ 366 วันอยู่แล้ว แต่กัน loop ยาวไว้ */
  const months = useMemo(() => {
    const out: { y: number; m: number }[] = []
    const [fy, fm] = from.slice(0, 7).split('-').map(Number)
    const end = to.slice(0, 7)
    for (let i = 0; i < 24; i++) {
      const d = new Date(Date.UTC(fy, fm - 1 + i, 1))
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
      if (key > end) break
      out.push({ y: d.getUTCFullYear(), m: d.getUTCMonth() + 1 })
    }
    return out
  }, [from, to])

  return (
    <div className={`plan-cal${loading ? ' is-loading' : ''}`}>
      {multiple ? (
        <div className="plan-cal__head">
          <span className="plan-cal__legend">
            {shiftIds.map((id, i) => (
              <span key={`${id}-${i}`} className="plan-cal__key" data-slot={i % 6}>
                สัปดาห์ {i + 1}:{' '}
                {id === null ? 'OFF' : (shifts.find((s) => s.Shift_ID === id)?.Shift_Desc ?? id)}
              </span>
            ))}
          </span>
        </div>
      ) : null}

      {months.map(({ y, m }) => (
        <MonthGrid
          key={`${y}-${m}`}
          y={y}
          m={m}
          byDate={byDate}
          multiple={multiple}
        />
      ))}

      {summary ? (
        <p className="plan-cal__foot">
          ทำงาน {nf.format(summary.workDays)} วัน · หยุด {nf.format(summary.holidays)} วัน
          {summary.skipped > 0 ? ` · ข้าม ${nf.format(summary.skipped)} วัน (มีใบลา)` : ''}
          {summary.overwritePast > 0
            ? ` · • ทับวันที่มีเวลาตอกบัตร ${nf.format(summary.overwritePast)} วัน`
            : ''}
        </p>
      ) : null}
    </div>
  )
}

/** ตารางวันของเดือนเดียว — แยกออกมาเพื่อให้ PlanCalendar กางหลายเดือนได้ */
function MonthGrid({
  y,
  m,
  byDate,
  multiple,
}: {
  y: number
  m: number
  byDate: Map<string, PlanDay>
  multiple: boolean
}) {
  /* ขึ้นต้นวันจันทร์ — การวนกะนับสัปดาห์จากวันจันทร์ ถ้าปฏิทินขึ้นต้นวันอาทิตย์
     แถบสีของแต่ละสัปดาห์จะเหลื่อมกับแถว มองไม่ออกว่าสลับกะตรงไหน */
  const rows = useMemo(() => monthGrid(y, m, true), [y, m])

  return (
    <div className="plan-cal__month">
      <p className="plan-cal__title">
        {MONTH_TH[m - 1]} {y}
      </p>

      <div className="plan-cal__dow" aria-hidden="true">
        {DOW_TH_MON.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>

      <div className="plan-cal__grid">
        {rows.map((row, ri) =>
          row.map((cell, ci) => {
            if (cell === null) return <span key={`${ri}-${ci}`} className="plan-cal__empty" />
            const d = byDate.get(cell.iso)
            /* วันที่อยู่นอกช่วงที่เลือก — เดือนแรก/เดือนสุดท้ายมีเสมอ */
            if (!d) {
              return (
                <div key={cell.iso} className="plan-cal__day is-out">
                  <span className="plan-cal__num">{cell.day}</span>
                </div>
              )
            }
            const cls = d.skipped
              ? 'is-skipped'
              : d.isHoliday
                ? 'is-off'
                : 'is-work'
            return (
              <div key={cell.iso} className={`plan-cal__day ${cls}`} data-slot={multiple ? d.slot % 6 : undefined}>
                <span className="plan-cal__num">{cell.day}</span>
                {d.skipped ? (
                  <span className="plan-cal__note">{d.reason ?? 'ข้าม'}</span>
                ) : d.isHoliday ? (
                  <span className="plan-cal__note">หยุด</span>
                ) : (
                  <>
                    <span className="plan-cal__time">{d.start ?? '—'}</span>
                    <span className="plan-cal__time">{d.end ?? '—'}</span>
                  </>
                )}
                {d.needsRecalc ? <span className="plan-cal__flag" title="มีเวลาตอกบัตรแล้ว — จะคำนวณใหม่">•</span> : null}
              </div>
            )
          }),
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, unit }: { label: string; value: number; unit: string }) {
  return (
    <div className="plan-stat">
      <span className="plan-stat__label">{label}</span>
      <span className="plan-stat__value num">{nf.format(value)}</span>
      <span className="plan-stat__unit">{unit}</span>
    </div>
  )
}

function SkippedList({ preview }: { preview: Preview }) {
  return (
    <>
      <p className="warn">
        ข้าม {nf.format(preview.summary.skipped)} วันเพราะมีใบลาอนุมัติแล้ว —
        ถ้าต้องเปลี่ยนกะของวันพวกนี้จริง ให้ยกเลิกใบลาก่อน
      </p>
      <div className="table-wrap table-wrap--rows10">
        <table className="data">
          <thead>
            <tr>
              <th>พนักงาน</th>
              <th>วันที่</th>
              <th>เหตุผล</th>
            </tr>
          </thead>
          <tbody>
            {preview.skipped.map((s) => (
              <tr key={`${s.empCode}|${s.date}`}>
                <td>
                  {s.empCode} {s.empName}
                </td>
                <td className="nowrap">{s.date}</td>
                <td>{s.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview.skippedMore > 0 ? <p className="meta">และอีก {nf.format(preview.skippedMore)} วัน</p> : null}
    </>
  )
}

/** เลือกพนักงาน — ค้นแล้วติ๊ก · ใช้ endpoint เดียวกับหน้าข้อมูลพนักงาน */
function EmployeePicker({
  picked,
  onChange,
  companyCode,
}: {
  picked: EmployeeRow[]
  onChange: (rows: EmployeeRow[]) => void
  /** '' = ทุกบริษัทที่ session มีสิทธิ์ */
  companyCode: string
}) {
  const [q, setQ] = useState('')
  const [departCode, setDepartCode] = useState('')
  const [branchCode, setBranchCode] = useState('')
  const [rows, setRows] = useState<EmployeeRow[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [lookups, setLookups] = useState<{
    departments: { code: string; name: string | null }[]
    branches: { code: string; name: string | null }[]
  }>({ departments: [], branches: [] })

  useEffect(() => {
    employeesApi(api)
      .lookups()
      .then((d) => setLookups({ departments: d.departments, branches: d.branches }))
      .catch(() => {})
  }, [])

  /* กรองที่ server — 1,680 คนในฐาน ดึงมาทั้งหมดแล้วกรองในเบราว์เซอร์คือโหลด
     ข้อมูลที่ไม่ได้ใช้ 98% · pageSize 50 พอให้ "เลือกทั้งหมด" ได้ทีละแผนก */
  const search = useCallback(
    async (text: string, dept: string, branch: string, cc: string) => {
      setLoading(true)
      try {
        const d = await employeesApi(api).list({
          q: text,
          status: 'active',
          departCode: dept || undefined,
          branchCode: branch || undefined,
          companyCode: cc || undefined,
          pageSize: 50,
        })
        setRows(d.employees)
      } catch {
        setRows([])
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  /* หน่วงก่อนยิง — พิมพ์ทีละตัวอักษรแล้วยิงทุกตัวคือถล่ม DB ของ production เปล่า ๆ */
  useEffect(() => {
    const t = setTimeout(() => void search(q, departCode, branchCode, companyCode), 300)
    return () => clearTimeout(t)
  }, [q, departCode, branchCode, companyCode, search])

  const has = (code: string) => picked.some((p) => p.empCode === code)
  const toggle = (row: EmployeeRow) =>
    onChange(has(row.empCode) ? picked.filter((p) => p.empCode !== row.empCode) : [...picked, row])

  return (
    <>
      <p className="fieldset-title">พนักงาน {picked.length ? `(เลือกแล้ว ${picked.length} คน)` : ''}</p>

      {picked.length ? (
        <p className="chips">
          {picked.map((p) => (
            <button
              key={p.empCode}
              type="button"
              className="chip chip--pick"
              onClick={() => toggle(p)}
              title="เอาออก"
            >
              {p.empCode} {p.fullName} ✕
            </button>
          ))}
        </p>
      ) : null}

      <div className="emp-toolbar">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="ค้นรหัสหรือชื่อพนักงาน"
          aria-label="ค้นหาพนักงาน"
        />
        <select value={departCode} onChange={(e) => setDepartCode(e.target.value)} aria-label="แผนก">
          <option value="">ทุกแผนก</option>
          {lookups.departments.map((d) => (
            <option key={d.code} value={d.code}>
              {d.name ?? d.code}
            </option>
          ))}
        </select>
        <select value={branchCode} onChange={(e) => setBranchCode(e.target.value)} aria-label="สาขา">
          <option value="">ทุกสาขา</option>
          {lookups.branches.map((b) => (
            <option key={b.code} value={b.code}>
              {b.name ?? b.code}
            </option>
          ))}
        </select>
        {rows && rows.length > 0 ? (
          <button
            type="button"
            className="ghost"
            onClick={() => {
              const add = rows.filter((r) => !has(r.empCode))
              onChange([...picked, ...add])
            }}
          >
            เลือกทั้งหมดที่ค้นเจอ ({rows.length})
          </button>
        ) : null}
      </div>

      <div className="table-wrap table-wrap--rows10">
        <table className="data emp-pick">
          <thead>
            <tr>
              <th />
              <th>รหัส</th>
              <th>ชื่อ</th>
              <th>แผนก</th>
              <th>สาขา</th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).map((r) => (
              <tr key={r.empCode}>
                <td>
                  <input
                    type="checkbox"
                    checked={has(r.empCode)}
                    onChange={() => toggle(r)}
                    aria-label={`เลือก ${r.empCode} ${r.fullName ?? ''}`}
                  />
                </td>
                <td className="nowrap">{r.empCode}</td>
                {/* ชื่อแผนก/สาขาของจริงยาวมาก ("ศูนย์ปฏิบัติการเครือข่าย(Network Operation
                    Center)") ปล่อยให้ตกบรรทัดแล้วแถวสูง 3 บรรทัด เห็นคนได้ทีละ 4-5 คน
                    บีบเหลือบรรทัดเดียวแล้วตัดด้วย … · ตัวเต็มอยู่ใน title ให้ชี้ดูได้ */}
                <td className="cell-clip" title={r.fullName ?? ''}>
                  {r.fullName}
                </td>
                <td className="cell-clip" title={r.departName ?? ''}>
                  {r.departName}
                </td>
                <td className="cell-clip" title={r.branchName ?? ''}>
                  {r.branchName}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {loading ? <p className="meta">กำลังค้น…</p> : null}
      {rows && rows.length === 0 && !loading ? <p className="meta">ไม่พบพนักงาน</p> : null}
    </>
  )
}

/** เลือกกะ — โหมดกะเดียวเลือกได้ตัวเดียว โหมดสลับเลือกได้หลายตัว "ตามลำดับ" */

/**
 * แพทเทิร์นกะรายสัปดาห์ — หนึ่งแถวคือหนึ่งสัปดาห์ หมุนวนเมื่อครบ
 *
 * ทำแบบนี้เพราะตรงกับที่คนคิดจริง ("สัปดาห์นี้เข้าเช้า สัปดาห์หน้าเข้าบ่าย")
 * ของเดิมเป็น "เลือกกะหลายตัว + สลับทุกกี่สัปดาห์" ซึ่งต้องคิดสองชั้น
 *
 * 1 แถว = กะเดียวตลอด · 2 แถว = สลับสัปดาห์เว้นสัปดาห์
 * อยากสลับทุก 2 สัปดาห์ก็ใส่ซ้ำ [A, A, B, B]
 */
function PatternEditor({
  shifts,
  pattern,
  onChange,
}: {
  shifts: ShiftOption[]
  pattern: (number | null)[]
  onChange: (next: (number | null)[]) => void
}) {
  /* ยังไม่เคยเลือก = ให้มีช่องแรกรอไว้เลย ไม่ต้องกด "เพิ่มสัปดาห์" ก่อนถึงจะเริ่มได้ */
  const weeks = pattern.length ? pattern : [null]

  const setWeek = (i: number, v: string) =>
    onChange(weeks.map((x, idx) => (idx === i ? (v === '__off__' ? null : Number(v)) : x)))

  return (
    <div className="emp-form">
      <p className="fieldset-title">แพทเทิร์นกะ</p>
      <p className="field-note">
        หมุนวนรายสัปดาห์ — ครบแล้ววนกลับสัปดาห์ที่ 1 · วันหยุดประจำสัปดาห์มาจากตัวกะเอง
      </p>

      {weeks.map((sid, i) => (
        <div key={i} className="pattern-row">
          <span className="pattern-row__no" data-slot={i % 6}>
            สัปดาห์ {i + 1}
          </span>
          <select
            value={sid === null ? '__off__' : String(sid)}
            onChange={(e) => setWeek(i, e.target.value)}
            aria-label={`กะของสัปดาห์ที่ ${i + 1}`}
          >
            <option value="__off__">— OFF หยุดทั้งสัปดาห์ —</option>
            {shifts.map((s) => (
              <option key={s.Shift_ID} value={s.Shift_ID}>
                {s.Shift_Desc}
                {s.Shift_Start && s.Shift_End ? ` (${s.Shift_Start}–${s.Shift_End})` : ''}
              </option>
            ))}
          </select>
          {weeks.length > 1 ? (
            <button
              type="button"
              className="ghost tiny icon-only"
              onClick={() => onChange(weeks.filter((_, idx) => idx !== i))}
              aria-label={`ลบสัปดาห์ที่ ${i + 1}`}
              title="ลบสัปดาห์นี้"
            >
              <TrashIcon />
            </button>
          ) : (
            /* ช่องว่างกว้างเท่าปุ่ม — ไม่งั้นแถวเดียวจะกว้างไม่เท่าแถวอื่น */
            <span className="pattern-row__spacer" />
          )}
        </div>
      ))}

      <p>
        <button
          type="button"
          className="ghost"
          disabled={weeks.length >= 7}
          onClick={() => onChange([...weeks, weeks[weeks.length - 1] ?? null])}
        >
          + เพิ่มสัปดาห์ (หมุนกะ)
        </button>
      </p>
    </div>
  )
}
